import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@ouropix/ui';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@ouropix/ui';
import {
  Shield, ShieldAlert, Activity, Ban, AlertTriangle, ShieldCheck, Globe, MapPin,
  Search, RefreshCw, Clock, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { api } from '@/lib/api';
import { unwrapList } from '@/lib/api-helpers';
import { IPBlacklistTable } from '@/components/admin-ops/IPBlacklistTable';

export const Route = createFileRoute('/_admin/ip-security')({
  component: IpSecurityPage,
});

const PAGE_SIZE = 20;

interface IpAccessLog {
  id: string;
  ip_address: string;
  user_id: string | null;
  action_type: string;
  success: boolean;
  country: string | null;
  country_code: string | null;
  city: string | null;
  is_proxy: boolean | null;
  is_vpn: boolean | null;
  is_hosting: boolean | null;
  is_blacklisted: boolean | null;
  created_at: string | null;
}

interface IpBlacklist {
  ip_address: string;
  reason: string;
  source: string;
  blocked_at: string | null;
  expires_at: string | null;
  notes: string | null;
}

interface CountryStat {
  country: string;
  country_code: string;
  total: number;
  blocked: number;
  suspicious: number;
}

function getFlagEmoji(countryCode: string | null) {
  if (!countryCode || countryCode === '??') return '🌍';
  const codePoints = countryCode.toUpperCase().split('').map((c) => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

function getActionBadge(action: string) {
  const colors: Record<string, string> = {
    login: 'bg-blue-500/20 text-blue-400',
    signup: 'bg-green-500/20 text-green-400',
    otp_request: 'bg-purple-500/20 text-purple-400',
    otp_validation: 'bg-indigo-500/20 text-indigo-400',
    password_reset: 'bg-orange-500/20 text-orange-400',
    cpf_check: 'bg-cyan-500/20 text-cyan-400',
  };
  return colors[action] ?? 'bg-gray-500/20 text-gray-400';
}

function IpSecurityPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, actionFilter, statusFilter]);

  const { data: accessLogs = [], isLoading: logsLoading, refetch: refetchLogs } = useQuery<IpAccessLog[]>({
    queryKey: ['ip-access-logs', searchTerm, actionFilter, statusFilter],
    queryFn: () =>
      api.get('/admin/ip-security/access-logs', {
        params: {
          limit: 500,
          search: searchTerm || undefined,
          action_type: actionFilter !== 'all' ? actionFilter : undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
        },
      }).then((r) => (Array.isArray(r.data) ? r.data : r.data?.data ?? [])),
  });

  const { data: blacklist = [] } = useQuery<IpBlacklist[]>({
    queryKey: ['ip-blacklist'],
    queryFn: () => api.get('/fraud/ip/blacklist').then((r) => unwrapList(r.data, 'ips')),
  });

  const stats = {
    totalAccesses: accessLogs.length,
    blockedAttempts: accessLogs.filter((l) => l.is_blacklisted).length,
    suspiciousAttempts: accessLogs.filter((l) => l.is_vpn || l.is_proxy || l.is_hosting).length,
    successfulLogins: accessLogs.filter((l) => l.success && l.action_type.includes('login')).length,
    uniqueIps: new Set(accessLogs.map((l) => l.ip_address)).size,
    activeBlacklist: blacklist.length,
  };

  const countryStats: CountryStat[] = accessLogs.reduce<CountryStat[]>((acc, log) => {
    const country = log.country ?? 'Desconhecido';
    const existing = acc.find((c) => c.country === country);
    if (existing) {
      existing.total++;
      if (log.is_blacklisted) existing.blocked++;
      if (log.is_vpn || log.is_proxy || log.is_hosting) existing.suspicious++;
    } else {
      acc.push({
        country,
        country_code: log.country_code ?? '??',
        total: 1,
        blocked: log.is_blacklisted ? 1 : 0,
        suspicious: (log.is_vpn || log.is_proxy || log.is_hosting) ? 1 : 0,
      });
    }
    return acc;
  }, []).sort((a, b) => b.total - a.total).slice(0, 10);

  const totalPages = Math.ceil(accessLogs.length / PAGE_SIZE);
  const paginatedLogs = accessLogs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="h-7 w-7" />
            Segurança de IP
          </h1>
          <p className="text-muted-foreground">Monitoramento de acessos, geolocalização e blacklist</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetchLogs()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-400" />
              <span className="text-xs text-muted-foreground">Total Acessos</span>
            </div>
            <p className="text-2xl font-bold mt-1">{stats.totalAccesses.toLocaleString('pt-BR')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Ban className="h-4 w-4 text-red-400" />
              <span className="text-xs text-muted-foreground">Bloqueados</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-red-400">{stats.blockedAttempts.toLocaleString('pt-BR')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-400" />
              <span className="text-xs text-muted-foreground">Suspeitos</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-yellow-400">{stats.suspiciousAttempts.toLocaleString('pt-BR')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-green-400" />
              <span className="text-xs text-muted-foreground">Logins OK</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-green-400">{stats.successfulLogins.toLocaleString('pt-BR')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-purple-400" />
              <span className="text-xs text-muted-foreground">IPs Unicos</span>
            </div>
            <p className="text-2xl font-bold mt-1">{stats.uniqueIps.toLocaleString('pt-BR')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-orange-400" />
              <span className="text-xs text-muted-foreground">Blacklist Ativa</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-orange-400">{stats.activeBlacklist.toLocaleString('pt-BR')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Country Distribution */}
      {countryStats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Distribuição por País
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {countryStats.map((country) => (
                <div key={country.country} className="bg-muted/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{getFlagEmoji(country.country_code)}</span>
                    <span className="font-medium text-sm truncate">{country.country}</span>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total:</span>
                      <span className="font-medium">{country.total}</span>
                    </div>
                    {country.blocked > 0 && (
                      <div className="flex justify-between text-red-400">
                        <span>Bloqueados:</span><span>{country.blocked}</span>
                      </div>
                    )}
                    {country.suspicious > 0 && (
                      <div className="flex justify-between text-yellow-400">
                        <span>Suspeitos:</span><span>{country.suspicious}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Blacklist */}
      <IPBlacklistTable />

      {/* Access Logs */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Logs de Acesso
            </CardTitle>
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar IP, cidade..."
                  className="pl-9 w-48"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Ação" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas ações</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="signup">Cadastro</SelectItem>
                  <SelectItem value="otp_request">OTP Request</SelectItem>
                  <SelectItem value="otp_validation">OTP Validation</SelectItem>
                  <SelectItem value="password_reset">Reset Senha</SelectItem>
                  <SelectItem value="cpf_check">Verificação CPF</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="success">Sucesso</SelectItem>
                  <SelectItem value="failed">Falha</SelectItem>
                  <SelectItem value="blocked">Bloqueados</SelectItem>
                  <SelectItem value="suspicious">Suspeitos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : accessLogs.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Nenhum log encontrado</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>Localização</TableHead>
                      <TableHead>Ação</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Flags</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedLogs.map((log) => (
                      <TableRow key={log.id} className={log.is_blacklisted ? 'bg-red-500/5' : ''}>
                        <TableCell className="text-sm whitespace-nowrap">
                          {log.created_at
                            ? format(new Date(log.created_at), 'dd/MM/yy HH:mm:ss', { locale: ptBR })
                            : '-'}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{log.ip_address}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <span>{getFlagEmoji(log.country_code)}</span>
                            <span className="truncate max-w-24">{log.city ?? log.country ?? 'Desconhecido'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getActionBadge(log.action_type)}>{log.action_type}</Badge>
                        </TableCell>
                        <TableCell>
                          {log.is_blacklisted ? (
                            <Badge variant="destructive">Bloqueado</Badge>
                          ) : log.success ? (
                            <Badge className="bg-green-500/20 text-green-400">Sucesso</Badge>
                          ) : (
                            <Badge variant="secondary">Falha</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {log.is_vpn && <Badge variant="outline" className="text-yellow-400 border-yellow-400/50 text-xs">VPN</Badge>}
                            {log.is_proxy && <Badge variant="outline" className="text-orange-400 border-orange-400/50 text-xs">Proxy</Badge>}
                            {log.is_hosting && <Badge variant="outline" className="text-purple-400 border-purple-400/50 text-xs">Hosting</Badge>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <p className="text-xs text-muted-foreground">
                    Mostrando {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, accessLogs.length)} de {accessLogs.length}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs px-2 text-muted-foreground">{currentPage} / {totalPages}</span>
                    <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
